
import React, { useEffect, useState, useMemo, useRef } from "react";
import { apiGet, apiPost, apiPut } from "../api/http";
import "../styles/categories.css";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import { dateFormat } from "../utils/dateFormat";
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import InputAdornment from '@mui/material/InputAdornment';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';

// Utility hook to check if text is truncated
function useIsTruncated<T extends HTMLElement>() {
  const ref = useRef<T>(null);
  const [truncated, setTruncated] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (el) {
      setTruncated(el.scrollWidth > el.clientWidth);
    }
  });
  return [ref, truncated] as const;
}

// Row component for table to use hooks safely
const CategoryTableRow: React.FC<{ item: Category; openEditModal: (item: Category) => void }> = ({ item, openEditModal }) => {
  const [createdByRef, createdByTruncated] = useIsTruncated<HTMLSpanElement>();
  const [modifiedByRef, modifiedByTruncated] = useIsTruncated<HTMLSpanElement>();
  return (
    <tr key={item.categoryId}>
      <td>
        {item.categoryName?.replace(/\b\w/g, char => char.toUpperCase())}
      </td>
      <td>
        <Tooltip
          title={item.createdBy}
          arrow
          disableHoverListener={!createdByTruncated}
        >
          <span
            ref={createdByRef}
            style={{ display: 'inline-block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', verticalAlign: 'bottom' }}
          >
            {item.createdBy}
          </span>
        </Tooltip>
      </td>
      <td>
        {dateFormat(item.createdDate)}
      </td>
      <td>
        <Tooltip
          title={item.modifiedBy || ''}
          arrow
          disableHoverListener={!modifiedByTruncated}
        >
          <span
            ref={modifiedByRef}
            style={{ display: 'inline-block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', verticalAlign: 'bottom' }}
          >
            {item.modifiedBy ?? "-"}
          </span>
        </Tooltip>
      </td>
      <td>
        {dateFormat(item.modifiedDate) ?? "-"}
      </td>
      <td>
        <span
          className={
            item.status === "Active"
              ? "badge badge-active"
              : "badge badge-inactive"
          }
        >
          {item.status}
        </span>
      </td>
      <td className="action-cell">
        <Tooltip title="Edit Category" arrow>
          <button
            className="edit-btn"
            onClick={() => openEditModal(item)}
            tabIndex={0}
          >
            <LocalIcon src="/icons/EditIcon.svg" className="edit-icon" />
          </button>
        </Tooltip>
      </td>
    </tr>
  );
};

type Category = {
  categoryId: number;
  categoryName: string;
  createdBy: string;
  createdDate: string;
  modifiedBy: string | null;
  modifiedDate: string | null;
  status: string;
};

type LocalIconProps = {
    src: string;
    alt?: string;
    className?: string;
};

const LocalIcon: React.FC<LocalIconProps> = ({ src, alt = "", className }) => (
    <img
        src={src}
        alt={alt}
        className={className}
        loading="lazy"
    />
);

const PAGE_SIZE = 10;

const Categories: React.FC = () => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortColumn, setSortColumn] = useState<keyof Category | null>("createdDate");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");

  const [currentPage, setCurrentPage] = useState(1);

  const [showModal, setShowModal] = useState(false);
  const [editItem, setEditItem] = useState<Category | null>(null);
  const [name, setName] = useState("");
  // Status filter for table: null = placeholder, 'Active'/'Inactive' = selected
  const [filterStatus, setFilterStatus] = useState<string | null>(null);
  // Status for modal form: always string
  const [status, setStatus] = useState<string>("Active");
  const [error, setError] = useState("");
  const [banner, setBanner] = useState<{ type: "success" | "error"; message: string } | null>(null);
    const [nameError, setNameError] = useState("");
    const [statusError, setStatusError] = useState("");
  useEffect(() => {
    fetchCategories();
  }, []);

const fetchCategories = async () => {
  try {
      const data = await apiGet<Category[]>('/activity-categories');
    setCategories(data ?? []);
  } catch (error) {
    console.error('Failed to load categories', error);
  } finally {
    setLoading(false);
  }
};
    const filteredData = useMemo(() => {
      let data = categories;
      // Only filter by status if a value is selected
      if (filterStatus) {
        data = data.filter((item: any) => item.status === filterStatus);
      }
      if (searchTerm) {
        const lower = searchTerm.toLowerCase();
        data = data.filter((item: any) => {
          return Object.entries(item).some(([key, val]: any) => {
            if (!val) return false;
            if (key.toLowerCase().includes("date")) {
              const formatted = new Date(val)
                .toLocaleDateString("en-GB")
                .toLowerCase();
              return (
                formatted.includes(lower) ||
                String(val).toLowerCase().includes(lower)
              );
            }
            return String(val).toLowerCase().includes(lower);
          });
        });
      }
      return data;
    }, [categories, searchTerm, filterStatus]);

    const sortedData = useMemo(() => {
        let data = [...filteredData];

        // ⭐ Default sort latest createdDate
        if (!sortColumn) {
            return data.sort(
                (a, b) =>
                    new Date(b.createdDate ?? 0).getTime() -
                    new Date(a.createdDate ?? 0).getTime()
            );
        }

        data.sort((a: any, b: any) => {
            let valA = a?.[sortColumn];
            let valB = b?.[sortColumn];

            // null handling
            if (valA == null) return 1;
            if (valB == null) return -1;

            // 🗓 DATE sort
            if (String(sortColumn).toLowerCase().includes("date")) {
                const t1 = new Date(valA).getTime();
                const t2 = new Date(valB).getTime();
                return sortDirection === "asc" ? t1 - t2 : t2 - t1;
            }

            // 🔢 NUMBER sort
            if (!isNaN(valA) && !isNaN(valB)) {
                return sortDirection === "asc"
                    ? Number(valA) - Number(valB)
                    : Number(valB) - Number(valA);
            }

            // 🔤 STRING sort
            return sortDirection === "asc"
                ? String(valA).localeCompare(String(valB))
                : String(valB).localeCompare(String(valA));
        });

        return data;
    }, [filteredData, sortColumn, sortDirection]);

    const totalPages = Math.ceil(sortedData.length / PAGE_SIZE);

    const paginatedData = sortedData.slice(
        (currentPage - 1) * PAGE_SIZE,
        currentPage * PAGE_SIZE
    );

    const handleSort = (column: keyof Category) => {
        setCurrentPage(1);   // ⭐ important

        if (sortColumn === column) {
            setSortDirection(sortDirection === "asc" ? "desc" : "asc");
        } else {
            setSortColumn(column);
            setSortDirection("asc");
        }
    };;

  const renderSortIcon = (column: keyof Category) => {
    const isActive = sortColumn === column;

    return (
      <span className="sort-icons">
        <ArrowUpwardIcon
          className={`sort-icon ${
            isActive && sortDirection === "asc" ? "active" : ""
          }`}
        />
        <ArrowDownwardIcon
          className={`sort-icon ${
            isActive && sortDirection === "desc" ? "active" : ""
          }`}
        />
      </span>
    );
  };
    const resetForm = () => {
        setName("");
      setStatus("");
        setEditItem(null);
        setError("");
        setNameError("");
        setStatusError("");
    };
    const openAddModal = () => {
        resetForm();
        setShowModal(true);
    };

  const openEditModal = (item: Category) => {
    resetForm();   // IMPORTANT
    setEditItem(item);
    setName(item.categoryName);
    setStatus(item.status);
    setShowModal(true);
  };

  const normalizeCategoryName = (value: string) => {
  return value
    .toLowerCase()
    .trim()
    .replace(/[\s\/\-]+/g, ""); // remove space, /, - completely
};

    const handleSave = async () => {
        if (!validateForm()) return;
    const trimmedName = name.trim();
    try {
      let response: Category;
      if (editItem) {
        response = await apiPut(`/activity-categories/${editItem.categoryId}`, {
          name: trimmedName,
          status,
        });
          setCategories(prev =>
              prev.map(c => c.categoryId === response.categoryId ? response : c)
          );

          setCurrentPage(1);   // ⭐ refresh pagination
       
        setBanner({ type: "success", message: "Category has been updated successfully" });
      } else {
        response = await apiPost('/activity-categories', {
          name: trimmedName,
          status,
        });
        setCategories(prev => [response, ...prev]);
        setSearchTerm(""); // Clear search
        setFilterStatus(null); // Clear filter
        setCurrentPage(1);   // ⭐ reset to first page after add
        setBanner({ type: "success", message: "New category has been added successfully" });
      }
      setShowModal(false);
      setEditItem(null);
      setName("");
      setStatus("");
      setTimeout(() => setBanner(null), 3000);
    } catch (err) {
      setBanner({ type: "error", message: "An error occurred. Please try again." });
      setTimeout(() => setBanner(null), 3000);
      console.error('Save failed', err);
    }
  };

    const validateForm = () => {
        let valid = true;

        const trimmed = name.trim();

        // Name required
        if (!trimmed) {
          setNameError("Category name is required");
          valid = false;
        }
        // min length
        else if (trimmed.length < 3) {
          setNameError("Minimum 3 characters required");
          valid = false;
        }
        // max length
        else if (trimmed.length > 30) {
          setNameError("Please limit to 30 characters");
          valid = false;
        }
        // only special chars/numbers
       else if (!/^[a-zA-Z0-9\s\/\-]+$/.test(trimmed)) {
           setNameError("Only letters, numbers, spaces, / and - are allowed");
          valid = false;
        }
        else {
          setNameError("");
        }

        // Duplicate check
        const normalizedInput = normalizeCategoryName(name.trim());

        const duplicate = categories.some(c => {
           const existing = normalizeCategoryName((c.categoryName ?? "").toString());
            return (
                existing === normalizedInput &&
                (!editItem || c.categoryId !== editItem.categoryId)
            );
        });

        if (duplicate) {
            setNameError("Category already exists");
            valid = false;
        }

        // Status validation
        if (!status) {
            setStatusError("Status required");
            valid = false;
        } else {
            setStatusError("");
        }

        return valid;
    };
  if (loading) return <div className="categories-loading">Loading...</div>;

  return (
      <div className="categories-container categories-layout">
    <div className="categories-top">
      <h2 className="categories-heading">Categories</h2>

      <div className="categories-controls">
        <button className="btn-primary" onClick={openAddModal}>
          <LocalIcon src="/icons/AddIcon.svg" className="btn-icon" />
          Add Category
        </button>

        {/* Status Filter Dropdown */}
        {/* Enhanced Status Filter Dropdown */}
        <Autocomplete
          className="filter-autocomplete status-filter"
          options={Array.from(new Set(categories.map((item) => item.status)))}
          value={filterStatus}
          onInputChange={(_event, newInputValue) => {
            // This ensures dropdown options filter as user types
            // No-op: Autocomplete handles filtering by default
          }}
          onChange={(_event, newValue) => {
            setFilterStatus(newValue);
            setCurrentPage(1);
          }}
          filterOptions={(options, { inputValue }) =>
            options.filter(option =>
              option.toLowerCase().includes(inputValue.toLowerCase())
            )
          }
          size="small"
          clearOnEscape
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder="Status"
              variant="outlined"
              className="filter-input"
              InputProps={{
                ...params.InputProps,
                startAdornment: (
                  <>
                    {params.InputProps.startAdornment}
                  </>
                ),
              }}
            />
          )}
        />

        <TextField
          size="small"
          placeholder="Search"
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setCurrentPage(1);
          }}
          className="input-search"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon className="search-icon" />
              </InputAdornment>
            ),
            endAdornment: (
              searchTerm ? (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    aria-label="clear search"
                    onClick={() => { setSearchTerm(''); setCurrentPage(1); }}
                    edge="end"
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : null
            ),
          }}
        />
      </div>
    </div>

      <div className="table-card">
        <table className="categories-table">
          <thead>
            <tr>
              <th onClick={() => handleSort("categoryName")}>
                <div className="th-inner">
                  Name {renderSortIcon("categoryName")}
                </div>
              </th>
              <th onClick={() => handleSort("createdBy")}>
                <div className="th-inner">
                  Created By {renderSortIcon("createdBy")}
                </div>
              </th>
              <th onClick={() => handleSort("createdDate")}>
                <div className="th-inner">
                  Created Date {renderSortIcon("createdDate")}
                </div>
              </th>
              <th onClick={() => handleSort("modifiedBy")}>
                <div className="th-inner">
                  Updated By {renderSortIcon("modifiedBy")}
                </div>
              </th>
              <th onClick={() => handleSort("modifiedDate")}>
                <div className="th-inner">
                  Updated Date {renderSortIcon("modifiedDate")}
                </div>
              </th>
              <th onClick={() => handleSort("status")}> <div className="th-inner">Status {renderSortIcon("status")}</div> </th>
              <th className="action-header">Action</th>
            </tr>
          </thead>

          <tbody>
            {paginatedData.length === 0 ? (
              <tr>
                <td colSpan={7} className="empty-state">
                     <div className="empty-content">
                       <p className="th-inner">No Categories found.<br/>
                      Try a different search or add a new category.
                       </p>
                     </div>
                </td>
              </tr>
            ) : (
              paginatedData.map((item) => (
                <CategoryTableRow key={item.categoryId} item={item} openEditModal={openEditModal} />
              ))
            )}
          </tbody>
        </table>
    </div>

    <div className="pagination-container">

      <div className="pagination-info">
        Showing{" "}
        {sortedData.length === 0
          ? 0
          : (currentPage - 1) * PAGE_SIZE + 1}{" "}
        to{" "}
        {Math.min(currentPage * PAGE_SIZE, sortedData.length)}{" "}
        of {sortedData.length} entries | Page {currentPage} of {totalPages || 1}
      </div>      

      <button
        className="page-btn prev-btn"
        disabled={currentPage === 1}
        onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
      >
        Previous
      </button>

      <div className="page-numbers">
        {Array.from({ length: Math.max(1, totalPages) }, (_, i) => i + 1).map((p) => (
          <button
            key={p}
            className={`page-number ${p === currentPage ? 'active' : ''}`}
            onClick={() => setCurrentPage(p)}
          >
            {p}
          </button>
        ))}
      </div>

      <button
        className="page-btn next-btn"
        disabled={currentPage === totalPages || totalPages === 0}
        onClick={() => setCurrentPage((p) => Math.min(totalPages || 1, p + 1))}
      >
        Next
      </button>
    </div>

    {showModal && (
      <div className="modal-overlay">
        <div className="modal">
                      <h3>{editItem ? "Edit Category" : "Add Category"}</h3>
                      <label>
                          Category Name <span className="required">*</span>
                      </label>
                        <input
                          value={name}
                          maxLength={30}
                          onChange={(e) => {
                          setName(e.target.value.replace(/\s{2,}/g, " "));
                            if (nameError) setNameError("");
                          }}
                          placeholder="Category Name"
                        />
                      {nameError && (
                          <div className="field-error">
                              {nameError}
                          </div>
                      )}
                      <label>
                          Status <span className="required">*</span>
                      </label>
                        <select
                          value={status ?? ''}
                          onChange={(e) => {
                            setStatus(e.target.value);
                            if (statusError) setStatusError("");
                          }}
                        >
                          <option value="">Select Status</option>
                          <option value="Active">Active</option>
                          <option value="Inactive">Inactive</option>
                      </select>

                      {statusError && (
                          <div className="field-error">
                              {statusError}
                          </div>
                      )}
                      {error && <div className="field-error">{error}</div>}
          <div className="modal-actions">
            <button className="btn-primary" onClick={handleSave}>
              {editItem ? "Update" : "Create"}
            </button>
           <button onClick={() => {
              resetForm();
              setShowModal(false);
            }}>
              Cancel
            </button>
          </div>
        </div>
      </div>
    )}

    {banner && (
      <div className={`toast-banner ${banner.type}`}>
        {banner.message}
      </div>
    )}
  </div>
);

};

export default Categories;
